import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, Link2Off, TriangleAlert } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import type {
  CompanyStatutoryLeaveTemplateInput, CompanyStatutoryLeaveTemplateView, LeaveTypeView,
} from '../../../models/attendance';
import { StatutoryFacts, StatutoryValueFields } from './statutoryFields';
import {
  readStatutoryDraft, toStatutoryDraft, type StatutoryDraft, type StatutoryDraftErrors,
} from './statutoryDraft';

const NOT_LINKED = 'none';

interface CopyRowProps {
  copy: CompanyStatutoryLeaveTemplateView;
  leaveTypes: LeaveTypeView[];
  editing: boolean;
  busy: string | null;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (input: CompanyStatutoryLeaveTemplateInput) => Promise<boolean>;
  onArchive: () => void;
  onRestore: () => void;
  onReset: () => Promise<boolean>;
}

/** One company copy: its values, link state and actions, with the edit form opening in place of the row. */
export function CopyRow({
  copy, leaveTypes, editing, busy, onEdit, onCancelEdit, onSave, onArchive, onRestore, onReset,
}: CopyRowProps) {
  const { t } = useTranslation(['leaves', 'common']);
  const [draft, setDraft] = useState<StatutoryDraft>(() => toStatutoryDraft(copy));
  const [linkValue, setLinkValue] = useState<string>(copy.leaveTypeId != null ? String(copy.leaveTypeId) : NOT_LINKED);
  const [errors, setErrors] = useState<StatutoryDraftErrors>({});
  const [confirmingReset, setConfirmingReset] = useState(false);

  const rowId = `statutory-copy-${copy.id}`;
  const saving = busy === `save-${copy.id}`;
  const canReset = copy.isCustomized && copy.platformValues != null && !copy.isArchived;

  const startEdit = () => {
    setDraft(toStatutoryDraft(copy));
    setLinkValue(copy.leaveTypeId != null ? String(copy.leaveTypeId) : NOT_LINKED);
    setErrors({});
    setConfirmingReset(false);
    onEdit();
  };

  const save = async () => {
    const { values, errors: found } = readStatutoryDraft(draft, t);
    setErrors(found);
    if (!values) return;
    // The PUT replaces the link too: always send the picker's value, which starts at the copy's current link.
    await onSave({ ...values, leaveTypeId: linkValue === NOT_LINKED ? null : Number(linkValue) });
  };

  const linkOptions = [
    { value: NOT_LINKED, label: t('leaves:statutoryTemplates.notLinkedOption') },
    ...leaveTypes.filter(lt => lt.isActive).map(lt => ({ value: String(lt.id), label: lt.name })),
  ];
  // A broken link (archived or inactive type) is not in the live list; keep it selectable so an edit of the
  // values alone does not change the link behind the Hr's back.
  if (copy.leaveTypeId != null && !linkOptions.some(o => o.value === String(copy.leaveTypeId))) {
    linkOptions.push({
      value: String(copy.leaveTypeId),
      label: t('leaves:statutoryTemplates.linkBrokenOption', { name: copy.linkedLeaveTypeName ?? `#${copy.leaveTypeId}` }),
    });
  }

  if (editing) {
    return (
      <li className="bg-muted/40 px-4 py-5 sm:px-5" data-testid={`${rowId}-edit`}>
        <h3 className="mb-4 text-sm font-semibold text-foreground">
          {t('leaves:statutoryTemplates.editing', { name: copy.leaveTypeName })}
        </h3>
        <div className="flex flex-col gap-4">
          <Select
            id={`${rowId}-link`}
            label={t('leaves:statutoryTemplates.appliesTo')}
            description={t('leaves:statutoryTemplates.linkHint')}
            options={linkOptions}
            value={linkValue}
            onChange={(v: string) => setLinkValue(v || NOT_LINKED)}
            searchable={linkOptions.length > 8}
            disabled={saving}
            data-testid={`${rowId}-link`}
          />
          <StatutoryValueFields
            draft={draft}
            errors={errors}
            onChange={setDraft}
            platform={copy.platformValues}
            idPrefix={rowId}
            disabled={saving}
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={onCancelEdit} disabled={saving} data-testid={`${rowId}-cancel`}>
              {t('common:Cancel')}
            </Button>
            <Button onClick={save} loading={saving} disabled={saving} data-testid={`${rowId}-save`}>
              {t('leaves:statutoryTemplates.saveChanges')}
            </Button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="px-4 py-4 sm:px-5" data-testid={rowId}>
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-foreground" data-testid={`${rowId}-name`}>{copy.leaveTypeName}</h3>
            {copy.isCustomized && (
              <span
                className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                data-testid={`${rowId}-customized`}
              >
                {t('leaves:statutoryTemplates.customized')}
              </span>
            )}
            {copy.sourceArchived && (
              <span
                className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                data-testid={`${rowId}-source-archived`}
              >
                {t('leaves:statutoryTemplates.sourceArchived')}
              </span>
            )}
          </div>
          <StatutoryFacts values={copy} platform={copy.platformValues} testIdPrefix={rowId} />
          <LinkState copy={copy} rowId={rowId} />
          {copy.notes && <p className="max-w-prose text-sm text-muted-foreground">{copy.notes}</p>}
        </div>

        <div className="flex flex-wrap items-center gap-2 md:justify-end">
          {copy.isArchived ? (
            <Button
              variant="outline"
              size="sm"
              iconName="ArchiveRestore"
              onClick={onRestore}
              loading={busy === `restore-${copy.id}`}
              disabled={busy != null}
              data-testid={`${rowId}-restore`}
            >
              {t('common:Restore')}
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" iconName="Pencil" onClick={startEdit} disabled={busy != null} data-testid={`${rowId}-edit-btn`}>
                {t('common:Edit')}
              </Button>
              {canReset && !confirmingReset && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="RotateCcw"
                  onClick={() => setConfirmingReset(true)}
                  disabled={busy != null}
                  data-testid={`${rowId}-reset`}
                >
                  {t('leaves:statutoryTemplates.resetToPlatform')}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                iconName="Archive"
                onClick={onArchive}
                loading={busy === `archive-${copy.id}`}
                disabled={busy != null}
                data-testid={`${rowId}-archive`}
              >
                {t('common:Archive')}
              </Button>
            </>
          )}
        </div>
      </div>

      {confirmingReset && canReset && (
        <div
          className="mt-3 flex flex-col gap-3 rounded-md border border-border bg-muted/50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
          role="group"
          aria-label={t('leaves:statutoryTemplates.resetToPlatform')}
          data-testid={`${rowId}-reset-confirm`}
        >
          <p className="text-sm text-foreground">{t('leaves:statutoryTemplates.resetConfirm')}</p>
          <div className="flex shrink-0 gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmingReset(false)} disabled={busy != null}>
              {t('common:Cancel')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              loading={busy === `reset-${copy.id}`}
              disabled={busy != null}
              onClick={async () => { if (await onReset()) setConfirmingReset(false); }}
              data-testid={`${rowId}-reset-submit`}
            >
              {t('leaves:statutoryTemplates.resetSubmit')}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function LinkState({ copy, rowId }: { copy: CompanyStatutoryLeaveTemplateView; rowId: string }) {
  const { t } = useTranslation(['leaves']);
  if (copy.leaveTypeId == null) {
    return (
      <p className="flex items-start gap-2 text-sm text-muted-foreground" data-testid={`${rowId}-link-state`} data-link="none">
        <Link2Off className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{t('leaves:statutoryTemplates.notLinked')}</span>
      </p>
    );
  }
  if (copy.linkBroken) {
    return (
      <p className="flex items-start gap-2 text-sm text-destructive" data-testid={`${rowId}-link-state`} data-link="broken">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{t('leaves:statutoryTemplates.linkBroken', { name: copy.linkedLeaveTypeName ?? '' })}</span>
      </p>
    );
  }
  return (
    <p className="flex items-start gap-2 text-sm text-foreground" data-testid={`${rowId}-link-state`} data-link="linked">
      <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
      <span>{t('leaves:statutoryTemplates.linkedTo', { name: copy.linkedLeaveTypeName ?? '' })}</span>
    </p>
  );
}
